// FRONT-1698: the model window opened from the composer's model button. The
// body (`ModelPickerPanel`) is separate from the Radix shell because Radix
// portals render nothing on the server, and this repo's component tests render
// to a markup string — so the rows are only assertable through the panel.

import { useRef, useState, type DragEvent } from 'react';
import { CheckIcon, DotsSixVerticalIcon, MagnifyingGlassIcon, StarIcon } from '@phosphor-icons/react';
import { cn } from '~/lib/utils';
import { Input } from '~/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog';
import { ProviderBadge, RegionBadge, deriveRegion, modelSearchFilter } from '~/components/model-badges';
import {
  favouritesEndTarget,
  filterModels,
  reorderIds,
  shortModelName,
  splitFavourites,
  type PickerModelEntry,
  type PickerPrefs,
} from './model-picker-logic';

export interface ModelPickerPanelProps {
  prefs: PickerPrefs;
  /** Id of the explicit pick (the active row), or null when following the default. */
  pickId: string | null;
  /** Pick a model (id) or clear the pick (null → the Default row). */
  onPick: (id: string | null) => void;
  onReorder: (order: string[]) => void;
  /** Star or unstar a model; a starred one sits in the Favourites section at the top. */
  onToggleStar: (id: string) => void;
  /** Search box ref so the shell can focus it without a tooltip trigger. */
  searchRef?: React.Ref<HTMLInputElement>;
  /** Test seam: initial search text. */
  initialSearch?: string;
}

export function ModelPickerPanel({
  prefs,
  pickId,
  onPick,
  onReorder,
  onToggleStar,
  searchRef,
  initialSearch = '',
}: ModelPickerPanelProps) {
  const [search, setSearch] = useState(initialSearch);
  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const searching = search.trim().length > 0;
  const { favourites, others } = splitFavourites(prefs.models, prefs.starred);
  const starredSet = new Set(favourites.map((m) => m.id));
  const favRows = filterModels(favourites, search, modelSearchFilter);
  const otherRows = filterModels(others, search, modelSearchFilter);
  const fullOrder = prefs.models.map((m) => m.id);
  const defaultName = prefs.defaultModel ? shortModelName(prefs.defaultModel.displayName) : null;

  const endDrag = () => {
    setDragId(null);
    setOverId(null);
  };
  // A row reorders only within its own section: dropping a favourite among
  // the rest (or the reverse) would look like starring it, and it isn't.
  const sameSection = (a: string, b: string) => starredSet.has(a) === starredSet.has(b);
  const dropOn = (targetId: string | null) => {
    if (dragId && !searching) {
      const next = reorderIds(fullOrder, dragId, targetId);
      if (next !== fullOrder) onReorder(next);
    }
    endDrag();
  };
  const dropAtFavouritesEnd = () => {
    if (!dragId) return endDrag();
    dropOn(favouritesEndTarget(fullOrder, starredSet, dragId));
  };

  const renderRow = (m: PickerModelEntry) => {
    const active = m.id === pickId;
    const starred = starredSet.has(m.id);
    const name = shortModelName(m.displayName);
    const region = deriveRegion(m.id, m.region);
    return (
      <div
        key={m.id}
        data-testid={`model-picker-row-${m.id}`}
        data-active={active ? 'true' : 'false'}
        data-starred={starred ? 'true' : 'false'}
        draggable={!searching}
        onDragStart={(e: DragEvent<HTMLDivElement>) => {
          if (searching) return;
          e.dataTransfer.effectAllowed = 'move';
          e.dataTransfer.setData('text/plain', m.id);
          setDragId(m.id);
        }}
        onDragOver={(e) => {
          if (!dragId || searching) return;
          if (!sameSection(dragId, m.id)) {
            // Refused here: drop any highlight left on the last valid row.
            setOverId(null);
            return;
          }
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
          setOverId(m.id);
        }}
        onDrop={(e) => {
          e.preventDefault();
          if (dragId && sameSection(dragId, m.id)) dropOn(m.id);
          else endDrag();
        }}
        onDragEnd={endDrag}
        className={cn(
          'group flex items-center gap-1.5 rounded-lg border px-1.5 py-1.5 transition-colors',
          active ? 'border-primary bg-primary/10' : 'border-border hover:bg-muted/50',
          dragId === m.id && 'opacity-50',
          overId === m.id && dragId !== m.id && 'border-primary',
        )}
      >
        <span
          data-testid={`model-picker-handle-${m.id}`}
          aria-hidden="true"
          className={cn(
            'flex shrink-0 items-center text-muted-foreground',
            searching ? 'opacity-30' : 'cursor-grab',
          )}
        >
          <DotsSixVerticalIcon size={14} weight="bold" />
        </span>
        <button
          type="button"
          aria-pressed={active}
          onClick={() => onPick(m.id)}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
        >
          <ProviderBadge id={m.id} />
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="truncate text-sm">{name}</span>
            <span className="truncate text-[10px] text-muted-foreground">{m.displayName}</span>
          </span>
          {active && <CheckIcon size={14} weight="bold" className="shrink-0 text-primary" />}
          {/* Where the model is hosted (EU flag, or a Global chip). No region
              means unknown here, so no badge, never a guessed US flag. */}
          {region && <RegionBadge region={region} />}
        </button>
        {/* A sibling of the pick button, never inside it: one click stars,
            the other picks. Faint until the row is hovered or the star is on. */}
        <button
          type="button"
          data-testid={`model-picker-star-${m.id}`}
          aria-pressed={starred}
          // The full name: two hostings of one model share a short name.
          aria-label={starred ? `Remove ${m.displayName} from favourites` : `Add ${m.displayName} to favourites`}
          onClick={() => onToggleStar(m.id)}
          className={cn(
            'flex size-6 shrink-0 items-center justify-center rounded-md outline-none transition-colors',
            'focus-visible:ring-2 focus-visible:ring-ring/50',
            starred
              ? 'text-amber-500 hover:bg-amber-500/10'
              : 'text-muted-foreground/40 hover:bg-muted hover:text-foreground group-hover:text-muted-foreground',
          )}
        >
          <StarIcon size={14} weight={starred ? 'fill' : 'regular'} />
        </button>
      </div>
    );
  };

  return (
    <div className="flex min-h-0 flex-col gap-2">
      <div className="relative">
        <MagnifyingGlassIcon
          size={14}
          className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
        />
        <Input
          ref={searchRef}
          data-testid="model-picker-search"
          aria-label="Search models"
          placeholder="Search models"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-8"
        />
      </div>

      {/* Up to three columns, so a long catalogue fits without endless scrolling.
          A FIXED height, not a max: the window is centred on screen, so a list
          that shrank while filtering would move the whole window. */}
      <div data-testid="model-picker-list" className="grid h-[min(40rem,70vh)] grid-cols-1 content-start gap-1.5 overflow-y-auto pr-0.5 sm:grid-cols-2 lg:grid-cols-3">
        <button
          type="button"
          data-testid="model-picker-default-row"
          aria-pressed={pickId === null}
          onClick={() => onPick(null)}
          className={cn(
            'col-span-full flex items-center gap-2 rounded-lg border px-2.5 py-2 text-left text-sm transition-colors',
            pickId === null
              ? 'border-primary bg-primary/10 text-foreground'
              : 'border-border text-muted-foreground hover:bg-muted/50 hover:text-foreground',
          )}
        >
          <span className="flex-1 truncate">{defaultName ? `Default — ${defaultName}` : 'Default'}</span>
          {pickId === null && <CheckIcon size={14} weight="bold" className="text-primary" />}
        </button>

        {favRows.length === 0 && otherRows.length === 0 && (
          <p data-testid="model-picker-empty" className="col-span-full px-1 py-3 text-center text-xs text-muted-foreground">
            No model matches “{search.trim()}”.
          </p>
        )}

        {favRows.length > 0 && (
          <SectionLabel testId="model-picker-favourites-label">
            Favourites
          </SectionLabel>
        )}
        {favRows.map(renderRow)}
        {!searching && favRows.length > 0 && (
          // Drop zone below the last favourite: moves a dragged favourite to last place.
          <div
            data-testid="model-picker-favourites-drop-end"
            aria-hidden="true"
            className="col-span-full h-2 shrink-0"
            onDragOver={(e) => {
              if (!dragId || !starredSet.has(dragId)) return;
              e.preventDefault();
              setOverId(null);
            }}
            onDrop={(e) => {
              e.preventDefault();
              if (dragId && starredSet.has(dragId)) dropAtFavouritesEnd();
              else endDrag();
            }}
          />
        )}
        {favRows.length > 0 && otherRows.length > 0 && (
          <SectionLabel testId="model-picker-others-label" spaced>
            All models
          </SectionLabel>
        )}
        {otherRows.map(renderRow)}

        {!searching && fullOrder.length > 0 && (
          // Drop zone below the last row: appends the dragged model.
          <div
            data-testid="model-picker-drop-end"
            aria-hidden="true"
            className="col-span-full h-3 shrink-0"
            onDragOver={(e) => {
              if (!dragId || (starredSet.has(dragId) && others.length > 0)) return;
              e.preventDefault();
            }}
            onDrop={(e) => {
              e.preventDefault();
              if (dragId && !(starredSet.has(dragId) && others.length > 0)) dropOn(null);
              else endDrag();
            }}
          />
        )}
      </div>
    </div>
  );
}

/** A small heading inside the list grid, spanning every column. */
function SectionLabel({
  children,
  testId,
  spaced = false,
}: {
  children: React.ReactNode;
  testId: string;
  spaced?: boolean;
}) {
  return (
    <p
      data-testid={testId}
      className={cn(
        'col-span-full px-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground',
        spaced ? 'mt-2.5 border-t border-border pt-2.5' : 'mt-1',
      )}
    >
      {children}
    </p>
  );
}

/** True when an OS file drag (not a row reorder, which carries only text) is in flight. */
export function carriesFiles(dt: Pick<DataTransfer, 'types'> | null | undefined): boolean {
  return !!dt && Array.from(dt.types ?? []).includes('Files');
}

/**
 * Drag handler for the window's content: keeps the event out of the composer's
 * dropzone, and for an OS FILE drag also cancels the browser default — an
 * uncancelled drop of a file on a page that does not handle it navigates the
 * tab to that file. Row reorders (text only) are left to the row handlers.
 */
export function swallowDrag(e: Pick<DragEvent, 'preventDefault' | 'stopPropagation' | 'dataTransfer'>): void {
  e.stopPropagation();
  if (!carriesFiles(e.dataTransfer)) return;
  e.preventDefault();
  if (e.dataTransfer) e.dataTransfer.dropEffect = 'none';
}

interface ModelPickerDialogProps extends Omit<ModelPickerPanelProps, 'searchRef' | 'initialSearch'> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ModelPickerDialog({ open, onOpenChange, onPick, ...panel }: ModelPickerDialogProps) {
  const searchRef = useRef<HTMLInputElement>(null);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[min(64rem,calc(100vw-4rem))]"
        // The dialog is portalled but React still bubbles its events up the
        // component tree into the composer's file dropzone, whose dragover
        // would light up the "drop files" overlay for a row reorder.
        onDragEnter={(e) => e.stopPropagation()}
        onDragOver={swallowDrag}
        onDragLeave={(e) => e.stopPropagation()}
        onDrop={swallowDrag}
        // Focus the search box ourselves: the default initial focus would land
        // on whichever control comes first, and a Radix tooltip opened by
        // autofocus swallows the first Escape (CLAUDE.md).
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          searchRef.current?.focus();
        }}
      >
        <DialogHeader>
          <DialogTitle>Choose a model</DialogTitle>
          <DialogDescription className="sr-only">
            Pick the model for this conversation, star a model to keep it in Favourites at the top, or drag a row to reorder the list.
          </DialogDescription>
        </DialogHeader>
        <ModelPickerPanel
          {...panel}
          searchRef={searchRef}
          onPick={(id) => {
            onPick(id);
            onOpenChange(false);
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
